import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import type { ClipCue } from "../../services/generated/client.js";
import { playbackFor } from "../../lib/audio.js";
import { playWindow } from "../../services/spotifyPlayer.js";
import { JoinPanel } from "./JoinPanel.js";
import { LobbyPanel } from "./LobbyPanel.js";
import { RoundPanel } from "./RoundPanel.js";
import { HowToPlay } from "../HowToPlay.js";
import { useWalkthrough } from "./useWalkthrough.js";
import { useGame } from "./useGame.js";

/**
 * The player controller — the phone.
 *
 * Four states, in the order somebody meets them: no seat (join), connecting,
 * seated but not playing (pick what to play from, or wait), and playing.
 *
 * # The seat is still a cookie, even though joining now needs an account
 *
 * Two different credentials, and only the first has changed. ADR 0027 put an
 * approved account in front of the *join* (see [`JoinPanel`]); what comes back
 * is the same `HttpOnly` device cookie it always was, held by the browser on
 * its own. So a refresh mid-game is still a non-event rather than a recovery
 * path, and a phone that lost signal for an hour comes back as itself without
 * anybody signing in again.
 */
export function PartyPage() {
  const { t } = useTranslation();
  const { code } = useParams();
  const game = useGame();
  // ⚠️ **Before the early returns.** Hooks cannot be called conditionally, and
  // every branch below this returns — so asking about the walkthrough after one
  // of them would be a hook that sometimes runs.
  const walkthrough = useWalkthrough(!!game.view?.live && !!game.view.board);

  if (game.status === "no-seat") {
    return <JoinPanel code={code ?? ""} onJoined={game.reconnect} />;
  }

  if (game.status === "failed") {
    return (
      <main className="flex min-h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-semibold">{t("party.lost")}</h1>
        <p className="max-w-prose text-sm text-white/60">{game.error}</p>
        <button
          type="button"
          onClick={game.reconnect}
          className="min-h-12 rounded-lg border border-white/25 px-5 font-medium"
        >
          {t("party.retry")}
        </button>
      </main>
    );
  }

  // **The idle window closed.** Said plainly, with the way back in — and not as
  // an error, because nothing went wrong: a phone was put down for an hour. The
  // seat is an `HttpOnly` cookie (ADR 0007), so coming back is one tap and the
  // timeline is still there; nobody re-types a name.
  //
  // Deliberately not the `failed` screen above, which apologises, and
  // deliberately not a spinner, which would imply the machine is still trying.
  if (game.health === "expired") {
    return (
      <main className="flex min-h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-semibold">{t("party.expired.title")}</h1>
        <p className="max-w-prose text-sm text-white/60">{t("party.expired.lead")}</p>
        <button
          type="button"
          onClick={game.reconnect}
          className="min-h-14 rounded-lg bg-flash-500 px-6 text-lg font-semibold text-stage-950"
        >
          {t("party.expired.rejoin")}
        </button>
      </main>
    );
  }

  if (game.status === "connecting" || !game.link || !game.view) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <p className="text-sm text-white/50">{t("party.connecting")}</p>
      </main>
    );
  }

  return (
    <>
      {/* **Silent in couch mode**: the television is the sink, and six phones
          playing the same clip a few hundred milliseconds apart is exactly what
          couch mode exists to avoid. A couch party with no display open is
          therefore silence — deliberately, because the fix is opening the board
          and not a phone quietly covering for a setup mistake. */}
      {/* ⚠️ **A Spotify cue goes to the host's phone in both layouts** — phase
          19 decision 3. Only that device has a Spotify session and a Premium
          account, and it is what keeps the packaged television out of OAuth
          entirely (ADR 0005: `location.origin` there is the string `"null"`,
          so a redirect flow has nowhere to come back to). A *local* cue keeps
          the layout rule above, unchanged. */}
      <ClipPlayer
        cue={game.cue}
        layout={game.view.deviceLayout}
        isSpotifySink={
          game.view.lobby?.members.find(
            (member) => member.profile_id === game.link?.profileId,
          )?.role === "host"
        }
      />
      {/* A dropped socket says so **over** the board rather than replacing it.
          What a player is looking at is still the last true thing they were
          told, and a round they can watch is better than a spinner — the link
          recovers itself underneath (a fresh ticket; see gameLink). Before this,
          the same event surfaced as "Authentication required" on the next tap. */}
      {game.health === "reconnecting" && (
        <p
          role="status"
          className="sticky top-0 z-20 bg-stage-800/95 px-4 py-2 text-center text-sm font-medium text-flash-400 backdrop-blur"
        >
          {t("party.reconnecting")}
        </p>
      )}
      {/* 🔴 **The first game a profile plays** — phase 24. Over the board rather
          than instead of it, so the thing being explained is one tap away and
          the room is not waiting on somebody reading. Dismissed once, gone for
          good, and still in the menu afterwards.

          On a live game, never in the lobby: a tutorial while somebody is
          choosing a mode explains a thing that does not exist yet. */}
      {walkthrough.showing && (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-stage-950/98">
          <HowToPlay onDone={walkthrough.dismiss} />
        </div>
      )}
      {game.view.live && game.view.board ? (
        <RoundPanel link={game.link} view={game.view} />
      ) : game.view.lobby ? (
        // The lobby, on the same connection as the game (phase 10). Who is
        // here, which mode, which teams, what to play from — and it is where team mode
        // finally became reachable at all.
        <LobbyPanel
          link={game.link}
          lobby={game.view.lobby}
          code={code ?? ""}
          you={game.link.profileId}
        />
      ) : (
        <main className="flex min-h-full items-center justify-center p-6">
          <p className="text-sm text-white/50">{t("party.reading")}</p>
        </main>
      )}
    </>
  );
}

/**
 * The audio sink.
 *
 * # Why the phone plays it at all
 *
 * In couch mode the display is the sink and this would be six phones playing the
 * same clip a few hundred milliseconds apart, which is worse than one television
 * doing it. But silence because nobody opened the TV page is worse still, and
 * `/display` does not exist until phase 6 — so until there is something to
 * compare against, the phone plays. The plan leaves this open deliberately: it
 * is a decision for a room with real phones in it.
 *
 * # Autoplay
 *
 * A browser will refuse to start audio that no gesture asked for. Every clip in
 * a game follows a tap — "Play the next clip", or a placement that ended the
 * previous round — so in practice the gesture is there; a refusal is caught and
 * dropped rather than thrown, because a failed clip must not take the round with
 * it.
 */
/**
 * Whether this device should make a sound for a given cue.
 *
 * Two different rules, because the two providers have different constraints and
 * flattening them into one would get a party either silence or six phones:
 *
 * | cue | couch | remote |
 * |---|---|---|
 * | `local` | the display plays; phones stay quiet | every client plays its own |
 * | `spotify` | **the host's phone**, in both — decision 3 | |
 *
 * A local clip can play anywhere because every client can fetch it. A Spotify
 * track can only play where there is a Spotify session, which is one device.
 */
export function shouldPlayHere(
  provider: string,
  layout: string,
  isSpotifySink: boolean,
): boolean {
  if (provider === "spotify") {
    return isSpotifySink;
  }
  return layout !== "couch";
}

function ClipPlayer({
  cue,
  layout,
  isSpotifySink,
}: {
  cue: ClipCue | null;
  layout: string;
  isSpotifySink: boolean;
}) {
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const element = audio.current;
    if (!element || !cue) {
      return;
    }
    if (!shouldPlayHere(cue.audio.provider, layout, isSpotifySink)) {
      return;
    }

    // Phase 19d: a Spotify track is not a file this element can load. It plays
    // through the Web Playback SDK on the sink, and the 30 seconds becomes a
    // seek and a timed stop rather than a clip boundary.
    if (cue.audio.provider === "spotify") {
      const trackId = cue.audio.track_id;
      let cancel: (() => void) | null = null;
      let dropped = false;
      void playWindow(trackId, cue.seconds).then(
        (stop) => {
          // The round may already have moved on while Spotify was answering.
          if (dropped) {
            stop();
            return;
          }
          cancel = stop;
        },
        () => {
          // A sink that cannot play is not a reason to stop a round: the rest
          // of the table is still guessing from the screen, and the fault is
          // reported by the settings panel rather than mid-game.
        },
      );
      return () => {
        dropped = true;
        cancel?.();
      };
    }
    // Assigning `src` and calling `load()` is what makes a *repeat* of the same
    // digest play again — setting an identical `src` alone is a no-op, and the
    // second time somebody hears that track it would be silent.
    //
    // ⚠️ Phase 19b: a cue names its provider, because a party's deck unions the
    // local catalog with Spotify tracks. Only the local branch plays through
    // this element; the Spotify sink is 19d, and until it exists such a card is
    // never dealt.
    const play = playbackFor(cue.audio);
    if (play.kind !== "element") {
      return;
    }
    element.src = play.src;
    element.currentTime = 0;
    element.load();
    void element.play().catch(() => {
      // Autoplay refused, or the clip is missing. Neither is worth stopping a
      // round for.
    });

    const stop = window.setTimeout(() => {
      element.pause();
    }, cue.seconds * 1000);
    return () => {
      window.clearTimeout(stop);
    };
  }, [cue]);

  // No caption track: a music clip has no dialogue to caption, and the round
  // says in words what is playing and whose turn it is.
  return <audio ref={audio} preload="none" aria-hidden="true" />;
}
